import React, { FunctionComponent } from 'react'

import { useMediaQueryContext } from 'contexts/MediaQueryContext'

import { GetMyBucketListItemsQueryResult } from '../../hooks/useQueries'

import AdventureCard from '../../components/cards/AdventureCard'
import ListingCard from '../../components/cards/ListingCard'
import StoryCard from '../../components/cards/StoryCard'
import WithSelectEscape from '../../components/SelectEscape/WithSelectEscape'
import { normalizeKeys } from '../../components/utils/propsUtils'
import { mapGQLTypenameToGQLSearchable } from '../../dashboard/MyBucketlists/gql/getBucketListItemsIndexHelpers'

export type BucketListItemDatum = GetMyBucketListItemsQueryResult['data']['currentUser']['bucketList']['items']['edges'][0]['node']['item']
type BucketListItemPricing = GetMyBucketListItemsQueryResult['data']['currentUser']['bucketList']['items']['edges'][0]['node']['pricing']

const formatItemUrl = (url: string, searchQuery: string) => {
  if (searchQuery && searchQuery.length > 0) {
    return `${url}${searchQuery}`
  }

  return url
}

interface BucketListItemProps {
  item: BucketListItemDatum
  pricing: BucketListItemPricing
  bookingAdventuresIndex: GQL_BookingAdventuresIndex
  bucketListItemsIndex: GQL_BucketListItemsIndex
  searchQuery?: string
}

const BucketListItem: FunctionComponent<BucketListItemProps> = ({
  item,
  pricing,
  bookingAdventuresIndex,
  bucketListItemsIndex,
  searchQuery,
}) => {
  const { md: largerThanMd } = useMediaQueryContext()
  const isMobile = !largerThanMd

  const itemType = mapGQLTypenameToGQLSearchable(item.__typename)

  switch (item.__typename) {
    case 'Listing':
      return (
        <>
          <WithSelectEscape
            itemId={item.id}
            itemType={itemType}
            bucketListItemsIndex={bucketListItemsIndex}
          >
            <ListingCard
              key={item.id}
              listing={{
                ...item,
                pricing: normalizeKeys(pricing),
                url: formatItemUrl(item.url, searchQuery),
              }}
              asLink={isMobile}
            />
          </WithSelectEscape>
        </>
      )
    case 'Story':
      return (
        <>
          <WithSelectEscape
            itemId={item.id}
            itemType={itemType}
            bucketListItemsIndex={bucketListItemsIndex}
          >
            <StoryCard
              key={item.id}
              story={{
                ...item,
                url: formatItemUrl(item.url, searchQuery),
              }}
              listing={{
                ...item.featuredListing,
                pricing: normalizeKeys(
                  pricing
                ) as BucketListItemProps['pricing'],
              }}
              asLink={isMobile}
              portrait={false}
            />
          </WithSelectEscape>
        </>
      )
    case 'StoryAdventure':
      return (
        <>
          <WithSelectEscape
            itemId={item.id}
            itemType={itemType}
            bookingAdventuresIndex={bookingAdventuresIndex}
            bucketListItemsIndex={bucketListItemsIndex}
          >
            <AdventureCard {...item} asLink={isMobile} showReviewScore />
          </WithSelectEscape>
        </>
      )
    default:
      return null
  }
}

export default BucketListItem
